// ===========================================
// Calendar Display Settings Modal
// ===========================================
// Allows admins to configure how calendar events are displayed
// Settings: Label source, Background color source, Border color source
// Legend: Shows colors based on the current background color source

import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Typography,
  IconButton,
  Box,
  CircularProgress,
  Paper,
  Divider,
  Tabs,
  Tab,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Switch,
  FormControlLabel,
} from '@mui/material';
import { Close as CloseIcon, RestartAlt as ResetIcon, ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import type { SelectChangeEvent } from '@mui/material/Select';
import { getContrastTextColor } from '../../styles/brandColors';
import CalendarLegend, { type LegendInstrument, type LegendProgramLevel } from './CalendarLegend';
import ColorPickerField from '../common/ColorPickerField';
import { LessonType } from '../../api/admin.api';
import { Teacher } from '../../api/users.api';
import {
  SpecialEventColors,
  DEFAULT_SPECIAL_EVENT_COLORS,
  SpecialEventColorOverrides,
  DEFAULT_SPECIAL_EVENT_COLOR_OVERRIDES,
} from './calendar/calendarColors';

export type { SpecialEventColors, SpecialEventColorOverrides };
export { DEFAULT_SPECIAL_EVENT_COLORS, DEFAULT_SPECIAL_EVENT_COLOR_OVERRIDES };

// ===========================================
// TYPES
// ===========================================

export type LabelSource = 'lessonName' | 'teacherName' | 'instrumentName' | 'roomName' | 'programLevelName';
export type ColorSource = 'lessonType' | 'teacher' | 'instrument' | 'programLevel' | 'stream';
export type BorderColorSource = ColorSource | 'none';
export type TeacherNameFormat = 'full' | 'first' | 'last' | 'firstLastInitial' | 'firstInitialLast' | 'initials';

export interface CalendarDisplaySettings {
  labelSource: LabelSource;
  backgroundColorSource: ColorSource;
  borderColorSource: BorderColorSource;
  specialEventColors?: SpecialEventColors;
  specialEventColorOverrides?: SpecialEventColorOverrides;
  teacherNameFormat?: TeacherNameFormat;
}

export const DEFAULT_CALENDAR_DISPLAY_SETTINGS: CalendarDisplaySettings = {
  labelSource: 'lessonName',
  backgroundColorSource: 'lessonType',
  borderColorSource: 'teacher',
  specialEventColors: DEFAULT_SPECIAL_EVENT_COLORS,
  teacherNameFormat: 'full',
};

interface CalendarDisplaySettingsModalProps {
  open: boolean;
  settings: CalendarDisplaySettings;
  loading?: boolean;
  onClose: () => void;
  onSave: (settings: CalendarDisplaySettings) => void;
  hideHybridGroup?: boolean;
  onHideHybridGroupChange?: (value: boolean) => void;
  // Data for legend display
  lessonTypes?: LessonType[];
  teachers?: Teacher[];
  instruments?: LegendInstrument[];
  programLevels?: LegendProgramLevel[];
  // Optional: open directly to legend tab
  initialTab?: number;
}

// ===========================================
// CONSTANTS
// ===========================================

const LABEL_OPTIONS: { value: LabelSource; label: string }[] = [
  { value: 'lessonName', label: 'Lesson Name' },
  { value: 'teacherName', label: 'Teacher Name' },
  { value: 'instrumentName', label: 'Instrument' },
  { value: 'roomName', label: 'Room' },
  { value: 'programLevelName', label: 'Program Level' },
];

const BACKGROUND_COLOR_OPTIONS: { value: ColorSource; label: string }[] = [
  { value: 'lessonType', label: 'Lesson Type' },
  { value: 'teacher', label: 'Teacher' },
  { value: 'instrument', label: 'Instrument' },
  { value: 'programLevel', label: 'Program Level' },
  { value: 'stream', label: 'Stream' },
];

const BORDER_COLOR_OPTIONS: { value: BorderColorSource; label: string }[] = [
  { value: 'none', label: 'None' },
  { value: 'lessonType', label: 'Lesson Type' },
  { value: 'teacher', label: 'Teacher' },
  { value: 'instrument', label: 'Instrument' },
  { value: 'programLevel', label: 'Program Level' },
  { value: 'stream', label: 'Stream' },
];

const TEACHER_NAME_FORMAT_OPTIONS: { value: TeacherNameFormat; label: string; example: string }[] = [
  { value: 'full', label: 'Full Name', example: 'Sarah Wilson' },
  { value: 'first', label: 'First Name', example: 'Sarah' },
  { value: 'last', label: 'Last Name', example: 'Wilson' },
  { value: 'firstLastInitial', label: 'First Name + Last Initial', example: 'Sarah W.' },
  { value: 'firstInitialLast', label: 'First Initial + Last Name', example: 'S. Wilson' },
  { value: 'initials', label: 'Initials', example: 'S.W.' },
];

/**
 * Format a full name ("First Last") according to the given format
 */
export function formatTeacherName(fullName: string, fmt: TeacherNameFormat = 'full'): string {
  const parts = fullName.trim().split(/\s+/);
  const first = parts[0] || '';
  const last = parts.slice(1).join(' ') || '';
  switch (fmt) {
    case 'first':
      return first;
    case 'last':
      return last || first;
    case 'firstLastInitial':
      return last ? `${first} ${last[0]}.` : first;
    case 'firstInitialLast':
      return last ? `${first[0]}. ${last}` : first;
    case 'initials':
      return last ? `${first[0]}.${last[0]}.` : first[0] ? `${first[0]}.` : fullName;
    case 'full':
    default:
      return fullName;
  }
}

// Preview sample data with colors
const PREVIEW_DATA = {
  lessonName: 'Piano Basics',
  teacherName: 'Sarah Wilson',
  instrumentName: 'Piano',
  roomName: 'Room A',
  programLevelName: 'Beginner',
  colors: {
    lessonType: '#4580E4', // Blue
    teacher: '#96DAC9',    // Mint
    instrument: '#FFCE00', // Yellow
    programLevel: '#FFAE9E', // Coral
    stream: '#8B7EC8',     // Violet (preview only — real events use the stream's own colour)
  },
};

// ===========================================
// COMPONENT
// ===========================================

export default function CalendarDisplaySettingsModal({
  open,
  settings,
  loading = false,
  onClose,
  onSave,
  hideHybridGroup = false,
  onHideHybridGroupChange,
  lessonTypes = [],
  teachers = [],
  instruments = [],
  programLevels = [],
  initialTab = 0,
}: CalendarDisplaySettingsModalProps) {
  const [localSettings, setLocalSettings] = useState<CalendarDisplaySettings>(settings);
  const [activeTab, setActiveTab] = useState(initialTab);

  // Reset local settings and tab when modal opens
  useEffect(() => {
    if (open) {
      setLocalSettings(settings);
      setActiveTab(initialTab);
    }
  }, [open, settings, initialTab]);

  const handleChange = (field: keyof CalendarDisplaySettings) => (
    event: SelectChangeEvent<string>
  ) => {
    setLocalSettings((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const handleSave = () => {
    onSave(localSettings);
  };

  const handleResetToDefaults = () => {
    setLocalSettings(DEFAULT_CALENDAR_DISPLAY_SETTINGS);
  };

  const handleSpecialEventColorChange = (key: keyof SpecialEventColors) => (color: string | null) => {
    setLocalSettings((prev) => ({
      ...prev,
      specialEventColors: {
        ...DEFAULT_SPECIAL_EVENT_COLORS,
        ...prev.specialEventColors,
        [key]: color || DEFAULT_SPECIAL_EVENT_COLORS[key],
      },
    }));
  };

  // Get current special event colors with defaults
  const currentSpecialEventColors = {
    ...DEFAULT_SPECIAL_EVENT_COLORS,
    ...localSettings.specialEventColors,
  };

  // Get current overrides with defaults
  const currentOverrides = {
    ...DEFAULT_SPECIAL_EVENT_COLOR_OVERRIDES,
    ...localSettings.specialEventColorOverrides,
  };

  const handleSpecialEventOverrideChange = (key: keyof SpecialEventColorOverrides) => (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setLocalSettings((prev) => ({
      ...prev,
      specialEventColorOverrides: {
        ...DEFAULT_SPECIAL_EVENT_COLOR_OVERRIDES,
        ...prev.specialEventColorOverrides,
        [key]: e.target.checked,
      },
    }));
  };

  // Get preview label based on current setting
  const getPreviewLabel = () => {
    const raw = PREVIEW_DATA[localSettings.labelSource] || PREVIEW_DATA.lessonName;
    if (localSettings.labelSource === 'teacherName') {
      return formatTeacherName(raw, localSettings.teacherNameFormat || 'full');
    }
    return raw;
  };

  // Get preview background color based on current setting
  const getPreviewBackgroundColor = () => {
    return PREVIEW_DATA.colors[localSettings.backgroundColorSource] || PREVIEW_DATA.colors.lessonType;
  };

  // Get preview border color based on current setting
  const getPreviewBorderColor = () => {
    if (localSettings.borderColorSource === 'none') return undefined;
    return PREVIEW_DATA.colors[localSettings.borderColorSource];
  };

  return (
    <Dialog open={open} onClose={loading ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          Calendar Display Settings
          <IconButton size="small" onClick={onClose} disabled={loading}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        <Tabs
          value={activeTab}
          onChange={(_, newValue) => setActiveTab(newValue)}
          sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label="Display Settings" />
          <Tab label="Legend" />
        </Tabs>

        {/* Display Settings Tab */}
        {activeTab === 0 && (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Configure how events appear on the calendar by selecting which attribute drives each visual property.
            </Typography>

            <Box sx={{ display: 'flex', gap: 3 }}>
              {/* Settings */}
              <Stack spacing={3} sx={{ flex: 1 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Label</InputLabel>
                  <Select
                    value={localSettings.labelSource}
                    label="Label"
                    onChange={handleChange('labelSource')}
                  >
                    {LABEL_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl fullWidth size="small">
                  <InputLabel>Teacher Name Display</InputLabel>
                  <Select
                    value={localSettings.teacherNameFormat || 'full'}
                    label="Teacher Name Display"
                    onChange={handleChange('teacherNameFormat')}
                  >
                    {TEACHER_NAME_FORMAT_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                        <Typography
                          component="span"
                          variant="body2"
                          color="text.secondary"
                          sx={{ ml: 1 }}
                        >
                          ({option.example})
                        </Typography>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl fullWidth size="small">
                  <InputLabel>Background Color</InputLabel>
                  <Select
                    value={localSettings.backgroundColorSource}
                    label="Background Color"
                    onChange={handleChange('backgroundColorSource')}
                  >
                    {BACKGROUND_COLOR_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl fullWidth size="small">
                  <InputLabel>Border Color</InputLabel>
                  <Select
                    value={localSettings.borderColorSource}
                    label="Border Color"
                    onChange={handleChange('borderColorSource')}
                  >
                    {BORDER_COLOR_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Stack>

              {/* Preview */}
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                  Preview
                </Typography>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    backgroundColor: '#FCF6E6',
                    borderRadius: 1,
                    minHeight: 140,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Box
                    sx={{
                      backgroundColor: getPreviewBackgroundColor(),
                      color: getContrastTextColor(getPreviewBackgroundColor()),
                      border: getPreviewBorderColor() ? `3px solid ${getPreviewBorderColor()}` : 'none',
                      borderRadius: '4px',
                      padding: '8px 12px',
                      fontSize: '13px',
                      fontWeight: 500,
                      minWidth: 120,
                      textAlign: 'center',
                    }}
                  >
                    {getPreviewLabel()}
                  </Box>
                </Paper>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  Sample event using your settings
                </Typography>
              </Box>
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* View options — instant, no Save needed */}
            <FormControlLabel
              control={
                <Switch
                  checked={hideHybridGroup}
                  onChange={(e) => onHideHybridGroupChange?.(e.target.checked)}
                  size="small"
                />
              }
              label={
                <Typography variant="body2">Hide Hybrid Booking Placeholders</Typography>
              }
              sx={{ mb: 2, ml: 0 }}
            />

            <Divider sx={{ mb: 2 }} />

            {/* Special Event Colors Accordion */}
            <Accordion
              disableGutters
              elevation={0}
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                '&:before': { display: 'none' },
                mb: 2,
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  minHeight: 48,
                  '& .MuiAccordionSummary-content': { my: 0 },
                }}
              >
                <Typography variant="subtitle2">Calendar Event Colors</Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 0 }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                  Customize colors for hybrid sessions, placeholders, and meet & greets.
                </Typography>
                <Stack spacing={2}>
                  {(
                    [
                      { key: 'hybridGroup', label: 'Hybrid (Group Week)' },
                      { key: 'hybridIndividual', label: 'Hybrid (Individual)' },
                      { key: 'hybridPlaceholder', label: 'Booking Placeholder' },
                      { key: 'meetAndGreet', label: 'Meet & Greet' },
                    ] as { key: keyof SpecialEventColors; label: string }[]
                  ).map(({ key, label }) => {
                    const useProgramLevel = currentOverrides[key] ?? false;
                    return (
                      <Box key={key}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                          <Typography variant="body2">{label}</Typography>
                          <FormControlLabel
                            control={
                              <Switch
                                size="small"
                                checked={useProgramLevel}
                                onChange={handleSpecialEventOverrideChange(key)}
                              />
                            }
                            label={
                              <Typography variant="caption" color="text.secondary">
                                Use {localSettings.backgroundColorSource === 'programLevel' ? 'program level' : 'background'} color
                              </Typography>
                            }
                            labelPlacement="start"
                            sx={{ mr: 0, ml: 0 }}
                          />
                        </Box>
                        <Box sx={{ opacity: useProgramLevel ? 0.4 : 1, pointerEvents: useProgramLevel ? 'none' : 'auto' }}>
                          <ColorPickerField
                            label=""
                            value={currentSpecialEventColors[key]}
                            onChange={handleSpecialEventColorChange(key)}
                            showClear={false}
                          />
                        </Box>
                      </Box>
                    );
                  })}
                </Stack>
              </AccordionDetails>
            </Accordion>

            <Button
              variant="text"
              size="small"
              startIcon={<ResetIcon />}
              onClick={handleResetToDefaults}
              disabled={loading}
              sx={{ color: 'text.secondary' }}
            >
              Reset to Defaults
            </Button>
          </>
        )}

        {/* Legend Tab */}
        {activeTab === 1 && (
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Legend shows colors based on your display settings.
            </Typography>
            <CalendarLegend
              backgroundColorSource={localSettings.backgroundColorSource}
              borderColorSource={localSettings.borderColorSource}
              lessonTypes={lessonTypes}
              teachers={teachers}
              instruments={instruments}
              programLevels={programLevels}
              showSpecialEvents={true}
              specialEventColors={currentSpecialEventColors}
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={loading}
          startIcon={loading ? <CircularProgress size={16} /> : null}
        >
          {loading ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
